/**
 * React Query hooks for managing Page related data
 * These hooks provide clean interfaces for fetching, caching, and managing page API data using React Query.
 */

import useApiClients from '@hooks/useApiClients.tsx';
import {
  Deckline,
  GetPageQueryVariables,
  GetPagesByPageNameQueryVariables,
  LockEvent,
  Page,
} from '@services/api/deckManagement/graphql/schema/API';
import {
  getDecklines,
  getLockedPagesForDeck,
  getPage,
  getPagesByPageName,
} from '@services/api/deckManagement/graphql/schema/queries';
import { useQuery } from '@tanstack/react-query';
import {
  createQueryOrMutationKey,
  createStandardQueryOptions,
  StandardMutationOptions,
  StandardQueryOptions,
} from '@utils/reactQueryConfig.ts';

/**
 * Interface for common page API parameters that rely on PageId
 * @interface CommonPageApiParams
 * @property {string} pageId - The unique identifier for the Page
 * @property {StandardQueryOptions | StandardMutationOptions} options - Additional React Query options
 */
export interface CommonPageApiParams extends Omit<GetPageQueryVariables, 'page_id'> {
  pageId?: string;
  options?: StandardQueryOptions | StandardMutationOptions;
}

// Query keys for Page related caching
export const pageQueryKeys = {
  all: createQueryOrMutationKey('page'),
  page: (pageId: string) => createQueryOrMutationKey(...pageQueryKeys.all, pageId),
  // Pure invalidation prefix for all getPagesByPageName variants (any pageName/deckId). Used to
  // specifically refresh page-grain records — e.g. lock attributes — without touching sibling page
  // queries like decklines, which also are prefixed with 'page' (pageQueryKeys.all).
  pageGrainsAll: () => createQueryOrMutationKey(...pageQueryKeys.all, 'pageGrains'),
  getPagesByPageName: (pageName: string, deckId?: string) =>
    createQueryOrMutationKey(...pageQueryKeys.all, 'pageGrains', pageName, deckId),
  getDecklines: (pageId: string) => createQueryOrMutationKey(...pageQueryKeys.all, 'decklines', pageId),
  lockedPagesForDeck: (deckId: string) => createQueryOrMutationKey(...pageQueryKeys.all, 'lockedPagesForDeck', deckId),
};

/**
 * Hook to fetch a single Page by ID
 * @param pageId - The unique identifier for the page
 * @param options - Additional React Query options
 */
export const useGetPage = ({ pageId, options }: CommonPageApiParams) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: pageQueryKeys.page(pageId || ''),
    queryFn: async (): Promise<Page> => {
      if (!pageId) {
        throw new Error('Page ID is required');
      }

      // Retrieve Page info using the imported query
      const response = await deckManagementClient.graphql({
        query: getPage,
        variables: {
          page_id: pageId,
        },
      });

      if ('data' in response && response.data?.getPage) {
        return response.data.getPage as Page;
      }

      throw new Error('Failed to fetch page or page not found');
    },
    ...createStandardQueryOptions<Page>({
      ...(options as StandardQueryOptions),
      enabled: Boolean(pageId) && ((options as StandardQueryOptions)?.enabled ?? true),
    }),
  });
};

/**
 * Get Page Granularity Details give a provide unique Page Name
 * @param pageName - The unique Page Name to query
 * @param pageGrain - Optional Page Grain to filter the results
 * @param deckId - Optional Deck ID to filter the results
 * @param options -Additional React Query options
 */
export const useGetPagesByPageName = ({
  page_name: pageName,
  page_grain: pageGrain,
  deck_id: deckId,
  options,
}: CommonPageApiParams & GetPagesByPageNameQueryVariables) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: pageQueryKeys.getPagesByPageName(pageName || '', deckId || ''),
    queryFn: async (): Promise<Page[]> => {
      if (!pageName) {
        throw new Error('Page Name is required');
      }

      // Retrieve Page Granularity into using the imported query
      const response = await deckManagementClient.graphql({
        query: getPagesByPageName,
        variables: {
          page_name: pageName,
          page_grain: pageGrain,
          deck_id: deckId,
        },
      });

      if ('data' in response && response.data?.getPagesByPageName) {
        return response.data.getPagesByPageName as Page[];
      }

      throw new Error('Failed to fetch granularity pages or pages not found');
    },
    ...createStandardQueryOptions<Page[]>({
      ...(options as StandardQueryOptions),
      enabled: Boolean(pageName) && ((options as StandardQueryOptions)?.enabled ?? true),
    }),
  });
};

/**
 * Hook to fetch Decklines for a given Page ID
 * @param pageId - The unique identifier for the page
 * @param options - Additional React Query options
 */
export const useGetDecklines = ({ pageId, options }: CommonPageApiParams) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: pageQueryKeys.getDecklines(pageId || ''),
    queryFn: async (): Promise<Deckline[]> => {
      if (!pageId) {
        throw new Error('Page ID is required');
      }

      // Retrieve Deckline info for the Page
      const response = await deckManagementClient.graphql({
        query: getDecklines,
        variables: {
          page_id: pageId,
        },
      });

      if ('data' in response && response.data?.getDecklines) {
        return response.data.getDecklines as Deckline[];
      }

      throw new Error('Failed to fetch page decklines or decklines not found');
    },
    ...createStandardQueryOptions<Deckline[]>({
      ...(options as StandardQueryOptions),
      enabled: Boolean(pageId) && ((options as StandardQueryOptions)?.enabled ?? true),
    }),
  });
};

/**
 * Hook to fetch the logical pages in a deck that currently hold an active lock.
 * Returns one LockEvent per locked page (deduped across grains by the backend). Drives the
 * deck-lock button's disabled state and the "pages locked" banner.
 * @param deckId - The deck to check for locked pages
 * @param options - Additional React Query options
 */
export const useLockedPagesForDeck = ({ deckId, options }: { deckId?: string; options?: StandardQueryOptions }) => {
  const { deckManagementClient } = useApiClients();

  return useQuery({
    queryKey: pageQueryKeys.lockedPagesForDeck(deckId || ''),
    queryFn: async (): Promise<LockEvent[]> => {
      if (!deckId) {
        throw new Error('Deck ID is required');
      }

      const response = await deckManagementClient.graphql({
        query: getLockedPagesForDeck,
        variables: {
          deck_id: deckId,
        },
      });

      if ('data' in response && response.data?.getLockedPagesForDeck) {
        return response.data.getLockedPagesForDeck.filter((event): event is LockEvent => event !== null);
      }

      return [];
    },
    ...createStandardQueryOptions<LockEvent[]>({
      ...options,
      enabled: Boolean(deckId) && (options?.enabled ?? true),
    }),
  });
};
